/**
 * Minimal API client. Same origin, session cookie, CSRF header on writes.
 * No axios — fetch covers everything the app needs.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    detail: string,
  ) {
    super(detail);
  }
}

function readCookie(name: string): string {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : "";
}

/** The CSRF token for callers that build their own request (SSE streaming). */
export function readCsrfCookie(): string {
  return readCookie("csrftoken");
}

let csrfReady: Promise<void> | null = null;

/** Fetches the CSRF cookie once per page load, before the first write. */
export function ensureCsrf(): Promise<void> {
  csrfReady ??= fetch("/api/auth/csrf", { credentials: "same-origin" }).then(async (response) => {
    if (!response.ok) throw new ApiError(response.status, await parseDetail(response));
  }).catch((error: unknown) => {
    csrfReady = null;
    throw error;
  });
  return csrfReady;
}

async function parseDetail(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: string };
    if (body.detail) return body.detail;
  } catch {
    /* non-JSON error body */
  }
  return `Der Server antwortet mit HTTP ${response.status}.`;
}

export async function apiGet<T>(path: string): Promise<T> {
  const response = await fetch(path, { credentials: "same-origin" });
  if (!response.ok) throw new ApiError(response.status, await parseDetail(response));
  return (await response.json()) as T;
}

export async function apiSend<T>(
  method: "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  await ensureCsrf();
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": readCookie("csrftoken"),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new ApiError(response.status, await parseDetail(response));
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
